import Typograf from 'typograf';

/** Служебные обозначения пустоты, которые приходят в полях ФИО вместо отсутствующего значения */
const EMPTY_NAME_PLACEHOLDERS = new Set(['null', 'none', 'nan', 'undefined']);

/**
 * Возвращает осмысленную часть имени: пробельные значения и служебные обозначения пустоты
 * считаются незаполненными и отбрасываются.
 */
function normalizeNamePart(name?: string | null): string {
  const trimmed = name?.trim() ?? '';
  return EMPTY_NAME_PLACEHOLDERS.has(trimmed.toLowerCase()) ? '' : trimmed;
}

/**
 * Собирает короткое имя участника из известных частей.
 * Фамилия известна — «Фамилия И.О.»; фамилия неизвестна — имя и отчество полностью
 * (по одним инициалам участника не узнать); известных частей нет — пустая строка.
 */
export const getShortName = (firstName?: string | null, lastName?: string | null, middleName?: string | null) => {
  const surname = normalizeNamePart(lastName);
  const parts = surname
    ? [surname, `${cutName(firstName)}${cutName(middleName)}`]
    : [normalizeNamePart(firstName), normalizeNamePart(middleName)];
  return parts.filter(Boolean).join(' ');
};

/**
 * Собирает полное имя в порядке «фамилия имя отчество».
 * Незаполненные части опускаются — без лишних разделителей и пробелов по краям.
 */
export const getFullName = (firstName?: string | null, lastName?: string | null, middleName?: string | null) => {
  return [lastName, firstName, middleName].map(normalizeNamePart).filter(Boolean).join(' ');
};

function cutName(name?: string | null): string {
  const normalized = normalizeNamePart(name);
  return normalized ? normalized.charAt(0).toUpperCase() + '.' : '';
}

/**
 * Форматирует строку мобильного телефона РФ в формат: +7 (XXX) XXX-XX-XX.
 * Корректно обрабатывает ввод с '8' или только 10 цифрами.
 * * @param phoneString Строка с номером телефона (может содержать мусор).
 * @returns Отформатированная строка телефона или null, если формат недействителен.
 */
export function formatPhoneNumber(phoneString: string): string {
  if (!phoneString) {
    return '';
  }

  // 1. Очистка: оставляем только цифры
  const digits = phoneString.replace(/\D/g, '');

  // 2. Нормализация кода страны
  let normalizedDigits: string;

  if (digits.length === 11) {
    // Случаи: '79991234567' или '89991234567'
    if (digits.startsWith('8')) {
      // Заменяем '8' на '7' (например, '8999...' -> '7999...')
      normalizedDigits = '7' + digits.substring(1);
    } else if (digits.startsWith('7')) {
      normalizedDigits = digits;
    } else {
      // Неизвестный формат с 11 цифрами, который не начинается с 7 или 8
      return '';
    }
  } else if (digits.length === 10) {
    // Случай: только 10 цифр (например, '9991234567').
    // Добавляем код страны '7'
    normalizedDigits = '7' + digits;
  } else {
    // Другие длины (слишком короткий, слишком длинный или нероссийский код)
    return '';
  }

  // Финальная проверка: должен быть ровно 11 цифр и начинаться с '7'
  if (normalizedDigits.length !== 11 || !normalizedDigits.startsWith('7')) {
    return '';
  }

  // 3. Форматирование в требуемый вид: +7 (999) 999-88-88
  // Используем группы захвата в регулярном выражении:
  // Группа 1: код страны (7)
  // Группа 2: код оператора (999)
  // Группа 3: три цифры (999)
  // Группа 4: две цифры (88)
  // Группа 5: две цифры (88)
  const pattern = /(\d{1})(\d{3})(\d{3})(\d{2})(\d{2})/;

  return normalizedDigits.replace(pattern, '+$1 ($2) $3-$4-$5');
}

/**
 * Возвращает правильную форму слова в зависимости от числа для русского языка.
 * Учитывает склонение: 1 (именительный), 2-4 (родительный единственное), 5+ (родительный множественное).
 * Также учитывает исключения для 11-14 (всегда множественное число).
 *
 * @param n - Число
 * @param textForms - Массив из 3 форм: [единственное число, родительный единственное, родительный множественное]
 *                    Например: ['группа', 'группы', 'групп']
 * @returns Правильная форма слова
 */
export function declOfNum(n: number, textForms: [string, string, string]): string {
  const absN = Math.abs(n);
  const mod100 = absN % 100;
  const mod10 = absN % 10;

  // Исключения для 11-14 (всегда множественное число - третья форма)
  if (mod100 >= 11 && mod100 <= 14) {
    return textForms[2];
  }

  // 1, 21, 31, 41... (первая форма - единственное число)
  if (mod10 === 1) {
    return textForms[0];
  }

  // 2, 3, 4, 22, 23, 24... (вторая форма - родительный единственное)
  if (mod10 >= 2 && mod10 <= 4) {
    return textForms[1];
  }

  // 5, 6, 7, 8, 9, 10, 20, 25... (третья форма - родительный множественное)
  return textForms[2];
}

/**
 * Formats seconds to digital time format (MM:SS or HH:MM:SS)
 * @param totalSeconds - number of seconds
 * @returns formatted time string (e.g., "10:15" or "1:30:45")
 */
export const formatSecondsToTime = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.round(totalSeconds % 60);

  const pad = (n: number): string => n.toString().padStart(2, '0');

  if (hours > 0) {
    return `${hours}:${pad(minutes)}:${pad(seconds)}`;
  }
  return `${pad(minutes)}:${pad(seconds)}`;
};

/**
 * Разбирает запись времени (`MM:SS` или `H:MM:SS`) в число секунд.
 */
export const parseTimeToSeconds = (time: string): number => {
  const parts = time.split(':').map(Number);
  return parts.length === 3 ? parts[0] * 3600 + parts[1] * 60 + parts[2] : parts[0] * 60 + parts[1];
};

/**
 * Формат отображения интервала времени: `TIME` — длительность (`MM:SS`/`H:MM:SS`),
 * `SECOND` — число секунд.
 */
export type TimeIntervalFormat = 'TIME' | 'SECOND';

/**
 * Форматирует интервал времени (число секунд) в действующем формате отображения.
 * `SECOND` — само число секунд; единица измерения выводится подписью рядом с названием поля.
 */
export const formatTimeInterval = (totalSeconds: number, format: TimeIntervalFormat): string => {
  return format === 'SECOND' ? String(Math.round(totalSeconds)) : formatSecondsToTime(totalSeconds);
};

/** Число знаков долей секунды: границы событий записи хранятся с точностью до миллисекунды */
const SECOND_FRACTION_DIGITS = 3;

/**
 * Запись момента записи с долями секунды в действующем формате отображения: `TIME` — `MM:SS.ddd`
 * (`H:MM:SS.ddd`), `SECOND` — `S.ddd`. Незначащие нули долей опускаются, целая секунда пишется без них.
 * Обратный разбор — {@link parseTimeIntervalMs}: всё, что показано, принимается и при вводе.
 */
export const formatTimeIntervalMs = (totalMs: number, format: TimeIntervalFormat): string => {
  const ms = Math.max(0, Math.round(totalMs));
  const wholeSeconds = Math.floor(ms / 1000);
  const fraction = String(ms % 1000)
    .padStart(SECOND_FRACTION_DIGITS, '0')
    .replace(/0+$/, '');
  const whole = format === 'SECOND' ? String(wholeSeconds) : formatSecondsToTime(wholeSeconds);
  return fraction ? `${whole}.${fraction}` : whole;
};

/** Запись момента в формате `TIME`: необязательные часы, минуты, секунды и доли секунды через точку или запятую */
const TIME_WITH_FRACTION_PATTERN = /^(?:(\d+):)?(\d+):(\d{1,2})(?:[.,](\d{1,3}))?$/;
/** Запись момента в формате `SECOND`: целые секунды и доли секунды через точку или запятую */
const SECONDS_WITH_FRACTION_PATTERN = /^(\d+)(?:[.,](\d{1,3}))?$/;

/**
 * Разбирает запись момента записи с долями секунды ({@link formatTimeIntervalMs}) в миллисекунды.
 * Доли короче трёх знаков дополняются нулями справа (`1.5` — 1500 мс).
 * @returns Миллисекунды или `null`, если запись не соответствует формату
 */
export const parseTimeIntervalMs = (value: string, format: TimeIntervalFormat): number | null => {
  const text = value.trim();
  const toFractionMs = (fraction: string | undefined) => Number((fraction ?? '').padEnd(SECOND_FRACTION_DIGITS, '0'));

  if (format === 'SECOND') {
    const match = SECONDS_WITH_FRACTION_PATTERN.exec(text);
    return match ? Number(match[1]) * 1000 + toFractionMs(match[2]) : null;
  }

  const match = TIME_WITH_FRACTION_PATTERN.exec(text);
  if (!match) return null;
  const [, hoursText, minutesText, secondsText, fraction] = match;
  const hours = Number(hoursText ?? 0);
  const minutes = Number(minutesText);
  const seconds = Number(secondsText);
  // Секунды — всегда внутри минуты; минуты ограничены только при заданных часах
  if (seconds >= 60 || (hoursText !== undefined && minutes >= 60)) return null;
  return ((hours * 60 + minutes) * 60 + seconds) * 1000 + toFractionMs(fraction);
};

/**
 * Название поля с интервалом времени: при показе числом секунд дополняется подписью единицы измерения.
 */
export const withSecondsLabel = (label: string, format: TimeIntervalFormat, secondsSuffix: string): string =>
  format === 'SECOND' ? `${label} ${secondsSuffix}` : label;

/**
 * Форматирует число в процент
 * @param value - числовое значение
 */
export const formatPercent = (value: number): string => {
  return `${value.toFixed(1)}%`;
};

/** Неразрывный пробел */
const NBSP = '\u00A0';
/** Неразрывный дефис */
const NB_HYPHEN = '\u2011';

/**
 * Привязывает предлоги и союзы к следующему слову неразрывным пробелом,
 * а в словах с дефисом заменяет обычный дефис на неразрывный.
 */
export const formatTextTypography = (text: string): string => {
  let result = text.replace(
    /(\s|^)(и|а|в|к|о|с|у|я|на|не|ни|но|за|из|от|до|по|об|об|же|бы|ли|то|во|со|ко|ну|да|ой|ай|ох|ах|для|при|без|над|под|про|как|что|или|это|его|ещё|уже|так|тот|она|они|нет|вот)(\s)/gi,
    (_, before, word, _after) => `${before}${word}${NBSP}`,
  );
  result = result.replace(/(\p{L})-(\p{L})/gu, `$1${NB_HYPHEN}$2`);
  return result;
};

/** Побуквенная транслитерация кириллицы в латиницу для адресных слагов */
const TRANSLIT_LETTERS: Record<string, string> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ё: 'e',
  ж: 'zh',
  з: 'z',
  и: 'i',
  й: 'i',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'h',
  ц: 'c',
  ч: 'ch',
  ш: 'sh',
  щ: 'sch',
  ъ: '',
  ы: 'y',
  ь: '',
  э: 'e',
  ю: 'yu',
  я: 'ya',
};

/**
 * Приводит строку к адресному слагу: кириллица транслитерируется в латиницу, регистр —
 * к нижнему, любые прочие символы становятся дефисом (без повторов и по краям).
 * Строка без пригодных символов даёт пустой результат — чем его заменить, решает вызывающий.
 */
export const slugifyTranslit = (text: string): string => {
  const transliterated = Array.from(text.toLowerCase())
    .map((char) => TRANSLIT_LETTERS[char] ?? char)
    .join('');
  return transliterated.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
};

/**
 * Занимает свободный адресный слаг под название и возвращает его.
 *
 * Названия не уникальны, поэтому совпавший слаг разводится числовым суффиксом, который
 * наращивается до фактически свободного значения: суффикс сам может совпасть с готовым слагом
 * соседа («Отчёт» второй раз → `otchet-2`, при этом `otchet-2` уже занял «Отчёт 2»). Название
 * без пригодных символов даёт `fallback`. Выданный слаг добавляется в `usedSlugs` — набор
 * общий на всю раскладку адресов, поэтому вызывающему достаточно передавать его дальше.
 */
export const allocateSlug = (name: string, usedSlugs: Set<string>, fallback: string): string => {
  const base = slugifyTranslit(name) || fallback;
  let slug = base;
  let suffix = 1;
  while (usedSlugs.has(slug)) {
    suffix += 1;
    slug = `${base}-${suffix}`;
  }
  usedSlugs.add(slug);
  return slug;
};

const nbspTypograf = new Typograf({
  locale: ['ru', 'en-US'],
  disableRule: '*',
  enableRule: ['common/nbsp/*', 'ru/nbsp/*'],
});

/**
 * Применяет к тексту только правила расстановки неразрывных пробелов
 * через библиотеку typograf. Остальные правила (кавычки, тире, пунктуация,
 * замены символов) отключены.
 */
export const applyNbspTypography = (text: string): string => nbspTypograf.execute(text);
